import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthProvider';

// Import components
import TutorialSlideDeck from '@/components/tutorial/TutorialSlideDeck';
import TutorialProgress from '@/components/tutorial/TutorialProgress';
import TutorialNavigation from '@/components/tutorial/TutorialNavigation';
import { tutorialSlides } from '@/components/tutorial/tutorialData';

interface TutorialPageProps {
  onComplete?: () => void;
}

const TutorialPage: React.FC<TutorialPageProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [carouselApi, setCarouselApi] = useState(null);
  const { user, loading, refreshUser } = useAuth();
  
  useEffect(() => {
    if (!loading && !user?.id) {
      toast.error("You're not signed in properly. Please log in again.");
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const handleNext = () => {
    if (activeSlide < tutorialSlides.length - 1) {
      carouselApi?.scrollNext();
      setActiveSlide(activeSlide + 1);
    } else {
      completeOnboarding();
    }
  };

  const handlePrevious = () => {
    if (activeSlide > 0) {
      setActiveSlide(activeSlide - 1);
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };
  
  // Function to mark onboarding as complete
  const completeOnboarding = async () => {
    setIsCompleting(true);
    
    try {
      const { error: rpcError } = await supabase.rpc('complete_onboarding');
      if (rpcError) throw rpcError;
      
      await supabase.auth.updateUser({
        data: { onboarding_completed: true }
      });

      // Ensure public.users is also updated (route guards rely on it)
      if (user?.id) {
        const { error: userRowError } = await supabase
          .from('users')
          .update({ onboarding_completed: true })
          .eq('id', user.id);
        if (userRowError) throw userRowError;
      }
      
      // Explicitly refresh the user context
      await refreshUser();
      navigate('/home');
      
      // Call onComplete callback if provided
      if (onComplete) {
        onComplete();
      }
      
    } catch (error) {
      toast.error("There was a problem completing onboarding");
      console.error("Error completing onboarding:", error);
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col p-6">
      {/* Back button */}
      <button 
        onClick={() => navigate(-1)}
        className="self-start mb-6"
      >
        <ArrowLeft className="h-6 w-6" />
      </button>

      <div className="flex-1 flex flex-col">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6 text-center"
        >
          <h1 className="text-2xl font-bold mb-2">How On Wheels Works</h1>
          <p className="text-muted-foreground">Swipe to learn the key features</p>
        </motion.div>

        <div className="flex-1 flex flex-col justify-between">
          <TutorialSlideDeck 
            slides={tutorialSlides} 
            onSlideChange={setActiveSlide}
            onApiReady={setCarouselApi}
          />

          <div className="mt-8 space-y-6">
            {/* Progress dots */}
            <TutorialProgress 
              currentSlide={activeSlide} 
              totalSlides={tutorialSlides.length} 
            />

            <TutorialNavigation 
              currentSlide={activeSlide}
              totalSlides={tutorialSlides.length}
              onNext={handleNext}
              onSkip={handleSkip}
              isCompleting={isCompleting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialPage;
